import { useContext, useEffect, useState } from 'react'
import ChannelContext from '@jetshop/core/components/ChannelContext'
import { useDigifiSearchCore } from './Core/useDigifiSearchCore'

/**
 Sorts the categories by name and moves the category with the selected ID to the top.
 * @param {*} category 
 * @param {*} categoryId 
 * @param {*} locale 
 * @param {*} level 
 * @returns 
 */
const sortCategoriesByName = (category, categoryId, locale, level = 1) => {
   if (!category?.subCategories) return category

   category.subCategories.sort((a, b) => {
      // Move the category with the selected ID to the top except for the top-level category
      if (level > 1) {
         if (a?.id == categoryId) return -1
         if (b?.id == categoryId) return 1
      }
      return a.name.localeCompare(b?.name, locale)
   })

   // Recursively sort the subcategories
   category.subCategories = category?.subCategories?.map(subCategory =>
      sortCategoriesByName(subCategory, categoryId, locale, level + 1)
   )

   return category
}

const useDigifiCategoryTree = categoryId => {
   const { getCategoryByCatId } = useDigifiSearchCore()
   const [topLevelCategory, setTopLevelCategory] = useState(undefined)
   const [hasSubCategories, setHasSubCategories] = useState(undefined)
   const [activeCategory, setActiveCategory] = useState(undefined)
   const locale = useContext(ChannelContext)?.selectedChannel?.language?.culture

   useEffect(() => {
      if (!categoryId) return
      ;(async () => {
         const activeCategory = await getCategoryByCatId({
            categoryId: categoryId,
         })

         setActiveCategory(activeCategory)

            if (!activeCategory) {
               setTopLevelCategory(null)
               setHasSubCategories(false)
               return
            }

         // No need to fetch the categoryId if it is already a top-level category, we already have the data
         if (activeCategory?.level == 1) {
            setTopLevelCategory(
               sortCategoriesByName(
                  activeCategory,
                  categoryId,
                  locale
               )
            )
            setHasSubCategories(activeCategory?.hasSubCategories)
            return
         }

         if (!activeCategory?.topLevelId) {
            setTopLevelCategory(null)
            setHasSubCategories(false)
            return
         }

         // Fetch the top-level category
         const topLevelCategory = await getCategoryByCatId({
            categoryId: activeCategory?.topLevelId,
         })


         if (!topLevelCategory) {
            setTopLevelCategory(null)
            setHasSubCategories(false)
            return
         }

         const isBrandCategory = topLevelCategory?.id === "3833";
         if (isBrandCategory && topLevelCategory?.subCategories && activeCategory?.path) {
            // Filter subcategories that match the active category's path
            topLevelCategory.subCategories = topLevelCategory.subCategories.filter(subCategory => {
               if (!subCategory?.path) return false; // Filter out subcategories without a path
               // Check if the active category's path matches or starts with the subcategory's path
               return activeCategory.path === subCategory.path || activeCategory.path.startsWith(subCategory.path + '/');
            });
         }

         setHasSubCategories(topLevelCategory?.hasSubCategories)

         setTopLevelCategory(
            sortCategoriesByName(
               topLevelCategory,
               categoryId,
               locale
            )
         )
      })()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [categoryId])

   return {
      topLevelCategory,
      hasSubCategories,
      activeCategory,
   }
}

export default useDigifiCategoryTree
